import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useDocument } from '@/hooks/firestore';
import { useAuth } from '@/stores/auth';
import { Card, LoadingSkeleton } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input, Textarea } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { callSubmitOffer, callWithdrawOffer } from '@/lib/callables';
import {
  CATEGORY_LABELS,
  OFFER_MESSAGE_MAX_CHARS,
  formatZAR,
  zarCents,
  type OfferDoc,
  type RequestDoc,
} from '@makhe/shared';

export default function SellerRequestDetailPage() {
  const { requestId } = useParams();
  const { firebaseUser } = useAuth();
  const reqRef = requestId ? doc(db, 'requests', requestId) : null;
  const { data: request, loading } = useDocument<RequestDoc>(reqRef);
  const myOfferRef = requestId && firebaseUser ? doc(db, 'requests', requestId, 'offers', firebaseUser.uid) : null;
  const { data: myOffer } = useDocument<OfferDoc>(myOfferRef);

  const [price, setPrice] = useState('');
  const [when, setWhen] = useState('');
  const [note, setNote] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  if (loading) return <LoadingSkeleton className="m-6 h-40" />;
  if (!request) return <p className="p-6 font-display text-2xl">Not found.</p>;

  const hasLiveOffer = myOffer && myOffer.status !== 'withdrawn';
  const closed = request.status !== 'open';
  const isOwner = !!firebaseUser && firebaseUser.uid === request.buyerId;

  const submit = async () => {
    if (!price || parseFloat(price) <= 0) return toast.error('Add your price.');
    setBusy(true);
    try {
      await callSubmitOffer({
        requestId: request.id,
        priceZARcents: zarCents(parseFloat(price)),
        proposedStartMillis: when ? new Date(when).getTime() : undefined,
        availabilityNote: !when && note ? note : undefined,
        message: message.trim(),
      });
      toast.success('Offer sent! The buyer has been notified.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async () => {
    setBusy(true);
    try {
      await callWithdrawOffer({ requestId: request.id });
      toast.success('Offer withdrawn.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-6 py-8">
      <Link to="/seller/requests" className="text-sm text-makhe-muted">← Requests</Link>

      <span className="eyebrow mt-3 block">{CATEGORY_LABELS[request.category]}{request.campus ? ` · ${request.campus}` : ''}</span>
      <h1 className="mt-2 font-display text-3xl leading-tight">{request.title}</h1>
      <p className="mt-1 text-sm italic text-makhe-muted">from {request.buyerSnapshot.displayName}</p>
      {request.description && (
        <p className="mt-3 whitespace-pre-line text-[15px] leading-relaxed text-makhe-ink/85">{request.description}</p>
      )}

      {request.referenceImageURLs?.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {request.referenceImageURLs.map((url) => (
            <a key={url} href={url} target="_blank" rel="noreferrer" className="aspect-square overflow-hidden rounded-md bg-makhe-line/40">
              <img src={url} alt="Reference" loading="lazy" className="h-full w-full object-cover" />
            </a>
          ))}
        </div>
      )}

      <Card className="mt-4 space-y-1 text-sm">
        {request.budgetHintZARcents != null && <p><strong>Budget hint:</strong> {formatZAR(request.budgetHintZARcents)}</p>}
        <p>
          <strong>When:</strong>{' '}
          {request.preferredWindow?.flexible ? 'Flexible' : request.preferredWindow?.start?.toDate?.().toLocaleString?.() ?? 'Flexible'}
        </p>
        <p><strong>Where:</strong> {request.location.type.replace('_', ' ')}{request.location.residence && ` · ${request.location.residence}`}</p>
      </Card>

      {isOwner ? (
        <Card className="mt-6 space-y-2">
          <p className="text-sm text-makhe-muted">This is your own request — you can’t send yourself an offer.</p>
          <Link to={`/requests/${request.id}`} className="text-sm text-makhe-primary underline">
            Open it in My requests to see offers →
          </Link>
        </Card>
      ) : closed ? (
        <Card className="mt-6"><p className="text-sm text-makhe-muted">This request is closed.</p></Card>
      ) : hasLiveOffer ? (
        <Card className="mt-6 space-y-2">
          <h2 className="font-display text-lg">Your offer</h2>
          <p className="text-sm">
            {formatZAR(myOffer!.priceZARcents)} ·{' '}
            <span className="capitalize text-makhe-muted">{myOffer!.status}</span>
          </p>
          {myOffer!.message && <p className="text-sm text-makhe-ink/80">{myOffer!.message}</p>}
          {myOffer!.status === 'pending' && (
            <Button variant="ghost" loading={busy} onClick={withdraw}>Withdraw offer</Button>
          )}
        </Card>
      ) : (
        <Card className="mt-6 space-y-4">
          <h2 className="font-display text-xl">Send an offer</h2>
          <Input label="Your price (R)" type="number" min={0} value={price} onChange={(e) => setPrice(e.target.value)} placeholder="600" />
          <Input label="When you're free" type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} />
          {!when && (
            <Input label="…or a note on availability" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Weekends, or evenings after 6pm" />
          )}
          <Textarea
            label="Message"
            rows={3}
            maxLength={OFFER_MESSAGE_MAX_CHARS}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Hi! I do exactly this look. I can bring my own kit and come to your res."
          />
          <Button className="w-full" loading={busy} onClick={submit}>Send offer</Button>
        </Card>
      )}
    </section>
  );
}
